import { format, isValid, parse, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { DATE_FORMATS, type DateFormatType } from '../constants/date-formats';

type DateInput = Date | string | number;

export class DateUtils {
  static toDate(value: DateInput): Date {
    if (value instanceof Date) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = parseISO(value);
      return isValid(parsed) ? parsed : new Date(NaN);
    }

    const d = new Date(value);
    return isValid(d) ? d : new Date(NaN);
  }

  static isValidDate(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    return isValid(this.toDate(value as DateInput));
  }

  static now(): Date {
    return new Date();
  }

  static formatDate(
    date: DateInput,
    type: DateFormatType = 'DATE_TIME',
    options?: { strict?: boolean },
  ): string | null {
    const d = this.toDate(date);

    if (!isValid(d)) {
      if (options?.strict) {
        throw new Error('Invalid date provided to formatDate');
      }
      return null;
    }

    return format(d, DATE_FORMATS[type]);
  }

  static formatCustom(
    date: DateInput,
    pattern: string,
    options?: { strict?: boolean },
  ): string | null {
    const d = this.toDate(date);

    if (!isValid(d)) {
      if (options?.strict) {
        throw new Error('Invalid date provided to formatCustom');
      }
      return null;
    }

    return format(d, pattern);
  }

  static formatWithTZ(
    date: DateInput,
    pattern: string,
    timeZone: string,
    options?: { strict?: boolean },
  ): string | null {
    const d = this.toDate(date);

    if (!isValid(d)) {
      if (options?.strict) {
        throw new Error('Invalid date provided to formatWithTZ');
      }
      return null;
    }

    return formatInTimeZone(d, timeZone, pattern);
  }

  static formatUTC(
    date: DateInput,
    pattern: string,
    options?: { strict?: boolean },
  ): string | null {
    return this.formatWithTZ(date, pattern, 'UTC', options);
  }

  static toApiString(date: DateInput, options?: { strict?: boolean }): string | null {
    const d = this.toDate(date);

    if (!isValid(d)) {
      if (options?.strict) {
        throw new Error('Invalid date provided to toApiString');
      }
      return null;
    }

    return d.toISOString();
  }

  static fromApi(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const d = parseISO(value);
    return isValid(d) ? d : null;
  }

  static parseDate(
    value: string,
    type: DateFormatType = 'DATE_TIME',
    options?: { strict?: boolean },
  ): Date | null {
    if (!value) {
      return null;
    }

    const parsed = parse(value, DATE_FORMATS[type], new Date());

    if (!isValid(parsed)) {
      if (options?.strict) {
        throw new Error(`Invalid date string: ${value}`);
      }
      return null;
    }

    return parsed;
  }

  static tryParseISO(value: string): Date | null {
    const d = parseISO(value);
    return isValid(d) ? d : null;
  }
}
